import type { Transition } from "@uirouter/core";
import { SwRedux } from "app/services/SwRedux";
import { swSettings } from "app/common/services/swSettings";
import * as utils from "app/components/filters-bar/utils";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { onRedirectWebsiteAnalysis } from "./onRedirectWebsiteAnalysis";
import {
    EUrlKeys,
    saveFilterConsistencyAfterFirstNavigation,
    shouldApplyFilterConsistency,
} from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

const WEBSITE_PERFORMANCE_NO_WW_ACCESS = {
    duration: "3m",
    webSource: devicesTypes.TOTAL,
};

export const onRedirectWebsitePerformance = (): transitionHookFn => {
    return async (transition: Transition) => {
        const fromState = transition.from() as IRouterState;
        const toState = transition.to() as IRouterState;
        const toParams = transition.params("to");

        if (shouldApplyFilterConsistency(fromState, toParams, EUrlKeys.KEY)) {
            const swRedux = SwRedux.getInstance();
            const { isNewSession } = swRedux.getState().usersDigitalSuiteFiltersSelection;
            const hasWWCountryAccess = swSettings.allowedCountry(WORLDWIDE_COUNTRY_ID, "WebAnalysis");
            if (!hasWWCountryAccess) {
                if (isNewSession) {
                    const targetState = transition.to() as IRouterState;
                    const availableCountriesExceptWW = utils
                        .getCountries()
                        ?.filter((country) => country.id !== WORLDWIDE_COUNTRY_ID && country.permitted);
                    const modifiedParams = {
                        ...toParams,
                        country: availableCountriesExceptWW[0].id,
                        ...WEBSITE_PERFORMANCE_NO_WW_ACCESS,
                    };
                    saveFilterConsistencyAfterFirstNavigation(targetState, modifiedParams);
                    // in first session we want to give the first available country
                    return {
                        params: modifiedParams,
                    };
                } else {
                    // for users without WW we want to open this specific page with WW data and only 3M and Total filter
                    if (toParams.country === WORLDWIDE_COUNTRY_ID.toString()) {
                        if (
                            toParams.duration !== WEBSITE_PERFORMANCE_NO_WW_ACCESS.duration ||
                            toParams.webSource !== WEBSITE_PERFORMANCE_NO_WW_ACCESS.webSource
                        ) {
                            return {
                                params: {
                                    ...toParams,
                                    ...WEBSITE_PERFORMANCE_NO_WW_ACCESS,
                                },
                            };
                        }
                    } else {
                        return onRedirectWebsiteAnalysis()(transition);
                    }
                }
            } else {
                return onRedirectWebsiteAnalysis()(transition);
            }
        }
    };
};
